
import React from "react";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { 
  BarChart3, 
  CreditCard, 
  Receipt, 
  ArrowDownToLine, 
  ArrowUpFromLine,
  Users,
  Settings,
  FileSpreadsheet
} from "lucide-react";

interface FinanceiroTabsProps {
  tabAtiva: string;
  onChangeTab: (value: string) => void;
  variant?: 'tabs' | 'buttons';
  className?: string;
}

const FinanceiroTabs: React.FC<FinanceiroTabsProps> = ({ 
  tabAtiva, 
  onChangeTab, 
  variant = 'tabs',
  className
}) => {
  const navigate = useNavigate();
  
  const tabConfig = [
    { 
      value: "resumo", 
      label: "Resumo", 
      icon: BarChart3,
      path: "/financeiro"
    },
    { 
      value: "contas", 
      label: "Contas & Extratos", 
      icon: CreditCard,
      path: "/financeiro/contas"
    },
    { 
      value: "fluxo", 
      label: "Fluxo de Caixa", 
      icon: BarChart3,
      path: "/financeiro"
    },
    { 
      value: "recebimentos", 
      label: "Recebimentos", 
      icon: ArrowDownToLine,
      subItems: [
        { 
          label: "Agendar Recebimento", 
          path: "/financeiro/recebimentos/agendar" 
        },
        { 
          label: "Registrar Recebimento", 
          path: "/financeiro/recebimentos/receber" 
        }
      ]
    },
    { 
      value: "pagamentos", 
      label: "Pagamentos", 
      icon: ArrowUpFromLine,
      subItems: [
        { 
          label: "Agendar Pagamento", 
          path: "/financeiro/pagamentos/agendar" 
        },
        { 
          label: "Registrar Pagamento", 
          path: "/financeiro/pagamentos/pagar" 
        }
      ]
    },
    { 
      value: "cadastro", 
      label: "Cadastro", 
      icon: Users,
      path: "/financeiro" // Keep it on the same page, just changing tabs
    },
    { 
      value: "relatorios", 
      label: "Relatórios", 
      icon: FileSpreadsheet,
      path: "/financeiro/relatorios"
    },
    { 
      value: "configuracoes", 
      label: "Configurações", 
      icon: Settings,
      path: "/financeiro/configuracoes"
    }
  ];

  const renderTabs = () => {
    return tabConfig.map((tab) => {
      const Icon = tab.icon;
      const commonProps = {
        key: tab.value,
        className: `flex items-center gap-2 transition-colors ${
          variant === 'tabs' 
            ? (tabAtiva === tab.value 
                ? "border-b-2 border-blue-500 text-blue-600" 
                : "border-b-2 border-transparent text-gray-500 hover:text-gray-700")
            : (tabAtiva === tab.value 
                ? "text-blue-600 font-medium" 
                : "text-gray-600 hover:text-blue-500")
        }`
      };

      const handleClick = () => {
        if (tab.path) {
          navigate(tab.path);
        }
        onChangeTab(tab.value);
      };

      if (variant === 'tabs') {
        return (
          <TabsTrigger 
            {...commonProps}
            value={tab.value} 
            onClick={handleClick}
          >
            <Icon className="h-5 w-5 text-blue-500" />
            <span>{tab.label}</span>
          </TabsTrigger>
        );
      }

      // Button variant for navigation - horizontal arrangement with a cleaner look
      return (
        <div className="inline-block" key={tab.value}>
          <button 
            {...commonProps} 
            onClick={handleClick}
            className={`${commonProps.className} px-4 py-3 flex flex-col items-center justify-center transition-all duration-200`}
          >
            <Icon className={`h-5 w-5 ${tabAtiva === tab.value ? 'text-blue-600' : 'text-blue-500'} mb-1`} />
            <span className="text-xs whitespace-nowrap">{tab.label}</span>
          </button>
          {tab.subItems && tabAtiva === tab.value && (
            <div className="absolute mt-1 bg-white shadow-md rounded-md py-1 z-10 min-w-48 border border-gray-100">
              {tab.subItems.map((subItem) => (
                <button 
                  key={subItem.label}
                  onClick={() => navigate(subItem.path)}
                  className="w-full text-left text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 px-4 py-2 flex items-center"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-blue-400 mr-2"></span>
                  {subItem.label}
                </button>
              ))}
            </div>
          )}
        </div>
      );
    });
  };

  return variant === 'tabs' 
    ? <TabsList className={`w-full my-6 flex border-b border-gray-200 overflow-x-auto ${className}`}>{renderTabs()}</TabsList>
    : <div className={`grid grid-flow-col auto-cols-max gap-2 p-2 ${className}`}>{renderTabs()}</div>;
};

export default FinanceiroTabs;
